// Lib
import { get, isEmpty, head, remove } from 'lodash/fp';

// Utils
import getIsTargetOutOfView from '../getIsTargetOutOfView';
import getCompletelyOverflowingEdges from '../getCompletelyOverflowingEdges';
import getClampedEdgeCenter from '../getClampedEdgeCenter';
import getOverflowingCorner from '../getOverflowingCorner';
import getNearbyCorner from '../getNearbyCorner';

// Constants
import { RECT_CORNERS, RECT_EDGES } from '../../../../../../common/maths/geometry/rect/rectConstants';

const GROUP_SEPARATION_THRESHOLD_Y = 30;
const GROUP_SEPARATION_THRESHOLD_X = 30;

const getGroupSeparationThresholdForEdge = (edge) => {
    switch (edge) {
        case RECT_EDGES.left:
        case RECT_EDGES.right:
            return GROUP_SEPARATION_THRESHOLD_Y;
        default:
            return GROUP_SEPARATION_THRESHOLD_X;
    }
};

export default ({ pointers, layerRect }) => {
    const pointerCenters = {};

    const edges = {
        [RECT_EDGES.top]: [],
        [RECT_EDGES.right]: [],
        [RECT_EDGES.bottom]: [],
        [RECT_EDGES.left]: [],
    };

    const cornerGroups = {
        [RECT_CORNERS.TOP_RIGHT]: [],
        [RECT_CORNERS.BOTTOM_RIGHT]: [],
        [RECT_CORNERS.BOTTOM_LEFT]: [],
        [RECT_CORNERS.TOP_LEFT]: [],
    };

    const nearbyCornerGroups = {
        [RECT_CORNERS.TOP_RIGHT]: [],
        [RECT_CORNERS.BOTTOM_RIGHT]: [],
        [RECT_CORNERS.BOTTOM_LEFT]: [],
        [RECT_CORNERS.TOP_LEFT]: [],
    };

    // Find the centre point of each of the pointers
    // Find the layer edge the pointers are on
    pointers.forEach((pointer) => {
        const pointerId = pointer.id;
        const { targetRect } = pointer;

        const isOutOfView = getIsTargetOutOfView({ targetRect, layerRect });

        if (!isOutOfView) return;

        // getOverflowingCorner
        const overflowedCorner = getOverflowingCorner({ targetRect, layerRect });

        // This pointer is overflowing a corner, so it MUST be grouped into the corner
        if (overflowedCorner) {
            return cornerGroups[overflowedCorner].push(pointerId);
        }

        // There will be one and only one overflowed edge in this scenario
        const overflowEdges = getCompletelyOverflowingEdges({ targetRect, layerRect });
        const overflowEdge = head(overflowEdges);
        const nearbyCorner = getNearbyCorner({ targetRect, layerRect });

        if (nearbyCorner) {
            nearbyCornerGroups[nearbyCorner].push({ id: pointerId, edge: overflowEdge });
        }

        pointerCenters[pointerId] = getClampedEdgeCenter({ targetRect, layerRect, edge: overflowEdge });
        edges[overflowEdge].push(pointerId);
    });

    // Loop through the nearby corner groups
    for (const corner in nearbyCornerGroups) {
        if (!nearbyCornerGroups.hasOwnProperty(corner)) continue;

        const nearbyCornerGroupPointers = nearbyCornerGroups[corner];

        // If there's no nearby pointers for this corner, move onto the next
        if (isEmpty(nearbyCornerGroupPointers)) continue;

        const cornerGroup = cornerGroups[corner];
        const alreadyHasCornerPointer = !isEmpty(cornerGroup);

        // If we don't already have a corner pointer, and we only have a single pointer in this group, then leave
        // it to be grouped on the side it came from
        if (!alreadyHasCornerPointer && nearbyCornerGroupPointers.length === 1) continue;

        const firstEdge = head(nearbyCornerGroupPointers).edge;
        // We should merge the group if there's already a corner pointer, or there's two pointers in this group from
        // different edges
        const shouldGroupWithCorner =
            alreadyHasCornerPointer || nearbyCornerGroupPointers.some((pointer) => pointer.edge !== firstEdge);

        if (!shouldGroupWithCorner) continue;

        cornerGroups[corner] = cornerGroups[corner].concat(nearbyCornerGroupPointers.map(get('id')));

        // Remove each of these pointers from their edges
        nearbyCornerGroupPointers.forEach(({ id, edge }) => {
            edges[edge] = remove((pointerId) => pointerId === id, edges[edge]);
        });
    }

    // Groups will be an array of arrays
    let groups = [];

    for (const corner in cornerGroups) {
        if (!cornerGroups.hasOwnProperty(corner)) continue;

        const cornerPointerIds = cornerGroups[corner];

        if (isEmpty(cornerPointerIds)) continue;

        groups.push({ pointerIds: cornerPointerIds, groupType: corner });
    }

    // For each layer edge
    for (const edge in edges) {
        if (!edges.hasOwnProperty(edge)) continue;

        const edgePointers = edges[edge];
        if (isEmpty(edgePointers)) continue;

        const edgeSeparationThreshold = getGroupSeparationThresholdForEdge(edge);

        groups = edgePointers
            // Sort each edge's pointers based on their centre points
            .sort((pointerIdA, pointerIdB) => pointerCenters[pointerIdA] - pointerCenters[pointerIdB])
            // Find the distances between each successive pointer (we're starting at the first and moving down)
            .reduce((groupAcc, pointerId, index) => {
                const [currGroup] = groupAcc;

                // Starting a new group at index 0
                if (index === 0) {
                    const newGroup = { pointerIds: [pointerId], groupType: edge };
                    groupAcc.unshift(newGroup);
                    return groupAcc;
                }

                const prevPointerId = edgePointers[index - 1];
                const prevCenterPoint = pointerCenters[prevPointerId];
                const currCenterPoint = pointerCenters[pointerId];

                const dist = currCenterPoint - prevCenterPoint;

                // If the distance is within the threshold we're still in the previous group
                if (dist < edgeSeparationThreshold) {
                    currGroup.pointerIds.push(pointerId);
                } else {
                    // Otherwise we're starting a new group
                    const newGroup = { pointerIds: [pointerId], groupType: edge };
                    groupAcc.unshift(newGroup);
                }

                return groupAcc;
            }, groups);
    }

    return groups;
};
